import type { BoxT } from '@mentimeter/ragnar-ui/box';
import { Box } from '@mentimeter/ragnar-ui/box';
import { List } from '@mentimeter/ragnar-ui/list';
import { Text } from '@mentimeter/ragnar-ui/text';
import { TextMarkupRenderer } from '@mentimeter/tiptap/text-markup-renderer';
import { scaleBand } from 'd3-scale';
import { Fragment, useRef } from 'react';
import { CorrectnessIndicator } from '../components/CorrectnessIndicator';
import { AvatarStackHtml } from '../components/identified-responses/AvatarStackHtml';
import { Legend } from '../components/Legend';
import { TextFitGhost } from '../components/text-fit-ghost/TextFitGhost';
import type {
  Accessor,
  ChoicesData,
  ChoicesStaticOptions,
  Identity,
} from '../types';
import { VISUALIZATION_LABEL_FONTSIZES } from '../utils/constants';
import { getLongestLabel } from '../utils/get-longest-label';
import { getOpacity } from './utils';

const ITEM_SPACING = 10;

const ITEM_MARKER_COLUMN = 'auto';
const SCORE_COLUMN = '4ch';
const LABEL_COLUMN = '1fr';
const IDENTITIES_COLUMN = '3em';
const CORRECTNESS_COLUMN = '1.2em';

const getTemplateColumns = (
  hasIdentities: boolean,
): BoxT['gridTemplateColumns'] => {
  const columns = [ITEM_MARKER_COLUMN, SCORE_COLUMN, LABEL_COLUMN];
  if (hasIdentities) {
    columns.push(IDENTITIES_COLUMN);
  }
  columns.push(CORRECTNESS_COLUMN);
  return columns.join(' ');
};

interface LegendItem extends ChoicesStaticOptions {
  value: number;
  displayValue: string;
}

export interface PieLegendProps {
  shouldShowScores: boolean;
  items: LegendItem[];
  keyAccessor: Accessor<ChoicesStaticOptions | ChoicesData, string>;
  labelAccessor: Accessor<ChoicesStaticOptions, string>;
  correctAnswerAccessor: (d: ChoicesStaticOptions) => boolean;
  getOptionColor: (id: string) => string;
  legendWidth: number;
  boundedHeight: number;
  hoveredItemKey: string | null;
  setHoveredItemKey: (key: string | null) => void;
  disableAnimations: boolean;
  shouldShowCorrect: boolean;
  identities: { choiceId: number; participants: Identity[] }[] | null;
  onClickOption?: ((optionId: ChoicesStaticOptions['id']) => void) | undefined;
}

export const PieLegend = ({
  shouldShowScores,
  items,
  keyAccessor,
  labelAccessor,
  correctAnswerAccessor,
  getOptionColor,
  legendWidth,
  boundedHeight,
  hoveredItemKey,
  setHoveredItemKey,
  disableAnimations,
  shouldShowCorrect,
  identities,
  onClickOption,
}: PieLegendProps) => {
  const longestLabelId = getLongestLabel(items, labelAccessor, keyAccessor);
  const hasLabelsToResize = longestLabelId !== null;
  const labelsContainerRef = useRef<HTMLDivElement | null>(null);

  const yScale = scaleBand()
    .domain(items.map((item) => item.id.toString()))
    .range([0, boundedHeight]);
  const legendItemHeight = Math.floor(yScale.bandwidth()) - ITEM_SPACING;

  const hasIdentities = Boolean(identities);

  return (
    <List
      maxWidth={legendWidth}
      maxHeight={boundedHeight}
      ref={labelsContainerRef}
      gap="space1"
      style={{
        opacity: hasLabelsToResize ? 0 : 1,
        transition: 'opacity 0.5s ease',
      }}
    >
      {items.map((option, index) => {
        const id = keyAccessor(option);
        const label = labelAccessor(option);
        const color = getOptionColor(id);

        const opacity = getOpacity({
          currentItemKey: id,
          hoveredItemKey,
        });
        const isLongestLabel = longestLabelId === id;

        const identityData = identities?.find(
          ({ choiceId }) => choiceId.toString() === id,
        );

        return (
          <Fragment key={id}>
            {index !== 0 && <Legend.ItemDivider />}
            <Legend.Item
              data-testid={`pie-legend-item-${id}`}
              opacity={opacity}
              onMouseEnter={() => setHoveredItemKey(id)}
              onMouseLeave={() => setHoveredItemKey(null)}
              onClick={onClickOption && (() => onClickOption(option.id))}
              display="grid"
              gridTemplateColumns={getTemplateColumns(hasIdentities)}
              py="1%"
            >
              <Legend.ItemMarker color={color} />
              <Text
                fontSize="inherit"
                color="textWeaker"
                style={{
                  opacity: shouldShowScores ? 1 : 0,
                }}
              >
                {option.displayValue}
              </Text>
              <Box
                style={{
                  flexShrink: 1, // Prevents the legend item from overflowing the container
                  wordBreak: 'break-word',
                  maxHeight: `${legendItemHeight}px`,
                }}
              >
                {isLongestLabel && (
                  <TextFitGhost
                    width={legendWidth}
                    height={legendItemHeight}
                    text={label}
                    finalFontSizeContainerRef={labelsContainerRef}
                    fontSizes={VISUALIZATION_LABEL_FONTSIZES}
                    shouldResizeFontSizeContainerWhileIterating
                  >
                    {(ref) => (
                      <Text aria-hidden="true" ref={ref}>
                        {label}
                      </Text>
                    )}
                  </TextFitGhost>
                )}
                <Text
                  fontSize="inherit"
                  style={{ dominantBaseline: 'central' }}
                >
                  <TextMarkupRenderer content={label} />
                </Text>
              </Box>
              {hasIdentities && (
                <Text fontSize="inherit">
                  <Box
                    display="inline-block"
                    style={{ verticalAlign: 'middle' }}
                  >
                    <AvatarStackHtml
                      identities={identityData?.participants || []}
                      avatarRadius="1.3em"
                      avatarFontSize="0.5em"
                      totalVotes={option.value}
                    />
                  </Box>
                </Text>
              )}
              <Text fontSize="inherit">
                <CorrectnessIndicator
                  shouldShow={shouldShowCorrect}
                  isCorrect={correctAnswerAccessor(option)}
                  immediate={disableAnimations}
                  size="1em"
                  display="inline-block"
                />
              </Text>
            </Legend.Item>
          </Fragment>
        );
      })}
    </List>
  );
};
